/**
 * Community Invite Notification Component
 * Displays a notification for community invites with Accept/Reject buttons
 */

import { EllipsisVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAcceptInvite, useRejectInvite } from "@/features/communities/hooks/useCommunityInvites"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface CommunityInviteNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface InviteNotificationData {
  communityId?: string
  inviteId?: string
  inviterName?: string
  inviterUsername?: string
  communityName?: string
  communityHandle?: string
  communityCoverImageUrl?: string
  roleType?: string
  actionTaken?: "accepted" | "declined"
  actionTakenAt?: string
  city?: string
  state?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function CommunityInviteNotification({
  notification,
  onRead,
  onUnread,
}: CommunityInviteNotificationProps) {
  const navigate = useNavigate()

  const data = notification.data as InviteNotificationData

  const communityId = data.communityId
  const inviteId = data.inviteId || notification.entityId
  const actionTaken = data.actionTaken
  const isRead = notification.isRead

  const { acceptInvite, isAccepting } = useAcceptInvite(communityId || "")
  const { rejectInvite, isRejecting } = useRejectInvite(communityId || "")

  if (!notification.triggeredByUser || !notification.data || !inviteId || !communityId) {
    return null
  }

  const inviter = notification.triggeredByUser

  const communityName = data.communityName || "a community"
  const inviterUsername = data.inviterUsername
  const city = data.city
  const state = data.state
  const location = city && state ? `${city}, ${state}` : null
  const communityCoverImageUrl = data.communityCoverImageUrl

  const displayImageUrl = getUserDisplayImage(inviter)
  const displayName = inviter.name || "Someone"

  const isProcessing = isAccepting || isRejecting

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/profile/${inviter.id}`)
    if (!isRead) {
      onRead?.()
    }
  }

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/communities/${communityId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation()
    acceptInvite(inviteId)
    onRead?.()
  }

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation()
    rejectInvite(inviteId)
    onRead?.()
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col h-[524px] gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* User avatar */}
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleAvatarClick}
          >
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {inviterUsername && (
                <span className="text-stone text-[14px]">@{inviterUsername}</span>
              )}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              <span className="font-semibold">{displayName}</span> invited you to join{" "}
              <span className="font-medium cursor-pointer" onClick={handleCommunityClick}>
                {communityName}
              </span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Action Buttons or Status */}
      {actionTaken ? (
        <div className="text-[11px] text-stone font-medium px-15">
          {actionTaken === "accepted" ? "Accepted the invite" : "Declined the invite"}
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-2 px-15" onClick={e => e.stopPropagation()}>
          <Button
            size="sm"
            variant="primary"
            className="bg-white text-base px-6 uppercase text-[14px] leading-[20px]"
            onClick={handleAccept}
            disabled={isProcessing}
          >
            Join
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-1 border-white text-base px-5 uppercase text-[14px] leading-[20px]"
            onClick={handleReject}
            disabled={isProcessing}
          >
            Decline
          </Button>
        </div>
      )}

      {/* Community Card */}
      <div
        className="relative mt-2 md:h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden cursor-pointer border border-card"
        onClick={handleCommunityClick}
      >
        {communityCoverImageUrl ? (
          <img
            src={communityCoverImageUrl}
            alt={communityName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {communityName}
            </span>
            {location && (
              <span className="text-white/80 text-[13px] leading-[20px]">{location}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
